import CustomAxios from "./CustomAxios";

/*
 * Per-category trading hours, as the Category Settings screen uses them.
 *
 * The API keys a row on category name plus hub, where an empty hub means every
 * hub. Category Settings edits the every-hub row only: a category belongs to
 * several hubs at once there, so per-hub exceptions would need a second, hub
 * shaped screen. The API still carries them, and a hub row set through the API
 * wins over the one this screen writes.
 */

export const DEFAULT_TIMING = {
    rowId: null,
    enabled: false,      // off = this category follows the hub's own hours
    always_open: false,
    opens_at: "06:00",
    closes_at: "23:00",
};

const ENDPOINT = "category-timings/";

/* "06:00:00" (API) -> "06:00" (<input type="time">) */
export const toInputTime = (value) => (value ? String(value).slice(0, 5) : "");

/* "06:00:00" -> "6:00 AM", for reading rather than editing. */
export const to12Hour = (value) => {
    if (!value) return "";
    const [h, m] = String(value).split(":");
    const hour = Number(h);
    if (!Number.isFinite(hour)) return String(value);
    const suffix = hour >= 12 ? "PM" : "AM";
    const twelve = hour % 12 === 0 ? 12 : hour % 12;
    return `${twelve}:${m ?? "00"} ${suffix}`;
};

export const describeTiming = (row) => {
    if (!row || !row.is_active) return "Hub hours";
    if (row.always_open) return "Open all day";
    if (!row.opens_at || !row.closes_at) return "Hub hours";
    return `${to12Hour(row.opens_at)} – ${to12Hour(row.closes_at)}`;
};

const sameName = (a, b) =>
    String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/* Every-hub rows, keyed by lower-cased category name. */
export const fetchAllHubTimings = async () => {
    const res = await CustomAxios.get(ENDPOINT);
    const rows = Array.isArray(res.data) ? res.data : res.data?.results || [];
    const byName = {};
    rows.forEach((row) => {
        if (row.inventory == null) byName[String(row.category_name || "").trim().toLowerCase()] = row;
    });
    return byName;
};

export const fetchCategoryTiming = async (categoryName) => {
    try {
        const res = await CustomAxios.get(ENDPOINT);
        const rows = Array.isArray(res.data) ? res.data : res.data?.results || [];
        const row = rows.find((r) => r.inventory == null && sameName(r.category_name, categoryName));
        if (!row) return DEFAULT_TIMING;
        return {
            rowId: row.id,
            enabled: !!row.is_active,
            always_open: !!row.always_open,
            opens_at: toInputTime(row.opens_at) || DEFAULT_TIMING.opens_at,
            closes_at: toInputTime(row.closes_at) || DEFAULT_TIMING.closes_at,
        };
    } catch (error) {
        console.error("Could not load category timings:", error);
        return DEFAULT_TIMING;
    }
};

/*
 * Creates, updates or switches off this category's hours, and answers with the
 * state the form should now show.
 *
 * Switching the toggle off deactivates the row rather than deleting it, so the
 * hours someone typed are still there when they switch it back on.
 */
export const saveCategoryTiming = async (categoryName, timing) => {
    const name = String(categoryName || "").trim();
    if (!name) return timing;

    if (!timing.enabled) {
        if (timing.rowId) {
            await CustomAxios.patch(`${ENDPOINT}${timing.rowId}/`, { is_active: false });
        }
        return { ...timing, enabled: false };
    }

    const payload = {
        category_name: name,
        inventory: null,
        always_open: !!timing.always_open,
        is_active: true,
        opens_at: timing.always_open ? null : `${timing.opens_at}:00`,
        closes_at: timing.always_open ? null : `${timing.closes_at}:00`,
    };

    if (timing.rowId) {
        const res = await CustomAxios.patch(`${ENDPOINT}${timing.rowId}/`, payload);
        return { ...timing, rowId: res.data?.id ?? timing.rowId };
    }
    const res = await CustomAxios.post(ENDPOINT, payload);
    return { ...timing, rowId: res.data?.id ?? null };
};
